import React from 'react';
import {
  Zap, TreePine, Globe, Bot, BarChart3, Gamepad2,
  GraduationCap, ArrowRight, Sparkles, BookOpen, Music, Scroll,
  Book, Warehouse, ShoppingCart, Coins, TrendingUp, Wheat, Plane,
  Anchor, Rocket, Atom, Gem, Flame, Star, Languages, Heart,
  Map, FileText, Eye, Scan, DoorOpen, ArrowRightLeft, MessageSquare,
  Mail, Shield, Users, Lightbulb, Settings, Cloud, Newspaper, Utensils,
  Trophy, Train, Droplets, Beaker, Activity, Cpu, Twitter, Facebook, Clock,
  Download, Smartphone, Bell
} from 'lucide-react';

interface LandingPageProps {
  onLogin: () => void;
  onRegister: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onLogin, onRegister }) => {
  return (
    <div className="min-h-screen overflow-y-auto relative animated-gradient-bg">
      {/* Vibrant Animated Gradient Background - Same as Core Homepage */}
      <div className="color-splash-layer-1"></div>
      <div className="color-splash-layer-2"></div>

      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center px-4 py-12 z-10">
        {/* Mystical Floating Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-96 h-96 bg-purple-500 rounded-full blur-3xl opacity-10 -top-48 -left-48 animate-pulse-slow"></div>
          <div className="absolute w-96 h-96 bg-pink-500 rounded-full blur-3xl opacity-10 top-1/4 right-0 animate-pulse-slower"></div>
          <div className="absolute w-96 h-96 bg-blue-400 rounded-full blur-3xl opacity-10 -bottom-48 -right-48 animate-pulse-slow delay-1000"></div>
        </div>

        <div className="relative max-w-6xl mx-auto text-center z-10">
          {/* Logo & Title */}
          <div className="mb-8 animate-fade-in-up">
            <div className="flex items-center justify-center mb-6">
              <img 
                src="/ahantra.png" 
                alt="AhantraVyuh" 
                className="w-20 h-20 md:w-24 md:h-24 mr-4 drop-shadow-2xl animate-float filter brightness-110"
              />
              <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tight drop-shadow-2xl">
                AhantraVyuh
              </h1>
            </div>
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Sparkles className="w-5 h-5 text-yellow-300 animate-pulse-soft" />
              <p className="text-xl md:text-2xl text-purple-100 font-medium drop-shadow-lg">
                Swarm Intelligence • Digital Twin • Innovation
              </p>
              <Sparkles className="w-5 h-5 text-yellow-300 animate-pulse-soft delay-500" />
            </div>
          </div>

          {/* Tagline */}
          <p className="text-lg md:text-xl text-gray-100 mb-12 max-w-3xl mx-auto leading-relaxed drop-shadow-lg backdrop-blur-sm bg-black/20 p-4 rounded-2xl border border-white/10">
            An integrated <span className="text-yellow-300 font-semibold">Swarm Intelligence</span> powered <span className="text-cyan-300 font-semibold">Digital Twin</span> platform.
            Experience the future of collaborative intelligence through 46 interconnected applications working in harmony.
          </p>

          {/* CTA Heading */}
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 drop-shadow-2xl">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg md:text-xl text-gray-100 mb-8 drop-shadow-lg">
            Join 1000+ users experiencing the power of swarm intelligence
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col items-center justify-center gap-4 mb-16">
            <button
              onClick={onRegister}
              className="group relative px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold text-lg shadow-2xl hover:shadow-purple-500/60 transition-all duration-300 hover:scale-105 flex items-center space-x-2 w-full sm:w-auto border border-purple-400/30 hover:border-purple-400/60 backdrop-blur-sm"
            >
              <span>Create your free account</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>

            <p className="text-lg text-gray-100 mt-2">Already a member?</p>

            <button
              onClick={onLogin}
              className="px-8 py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-xl font-bold text-lg shadow-2xl hover:shadow-teal-500/60 transition-all duration-300 hover:scale-105 w-full sm:w-auto border border-teal-400/30 hover:border-teal-400/60 backdrop-blur-sm"
            >
              Sign In
            </button>
          </div>

          {/* Compliance Notice Card */}
          <div className="max-w-3xl mx-auto mt-10 mb-2">
            <a
              href="https://gyn.ahantravyuh.com/compliance.html"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between gap-4 w-full px-6 py-4 rounded-2xl border-2 border-red-400/60 bg-gradient-to-r from-red-900/70 via-red-800/70 to-red-900/70 backdrop-blur-sm shadow-lg shadow-red-900/40 hover:border-red-300/80 hover:shadow-red-700/50 transition-all duration-200 group"
              style={{ textDecoration: 'none' }}
            >
              <div className="flex items-center gap-4">
                <Shield className="w-7 h-7 text-red-300 flex-shrink-0" />
                <div className="text-left">
                  <div className="text-white font-bold text-base leading-tight">Regulatory Compliance Statement</div>
                  <div className="text-red-200 text-sm mt-0.5">AhantraVyuh Ltd &nbsp;·&nbsp; SC866275 &nbsp;·&nbsp; ICO C1925848 &nbsp;·&nbsp; Scotland, UK</div>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-red-300 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>

          {/* BIOFUEL Digital Twin - Featured Card */}
          <div className="max-w-6xl mx-auto mt-16 mb-16 biofuel-featured-card group">
            {/* Animated background glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 rounded-3xl blur-2xl opacity-20 group-hover:opacity-30 transition-opacity duration-500 animate-pulse-slow"></div>

            {/* Main card content */}
            <div className="relative bg-gradient-to-br from-emerald-500/20 via-teal-500/20 to-cyan-500/20 backdrop-blur-xl border-2 border-emerald-400/40 rounded-3xl p-8 md:p-12 shadow-2xl overflow-hidden">
              {/* Bioreactor background image */}
              <div
                className="absolute inset-0 rounded-3xl opacity-10 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: 'url(/bioreactor-bg.png)' }}
              ></div>
              {/* Floating particles effect */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-emerald-300/20 to-transparent rounded-full blur-3xl animate-float"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-cyan-300/20 to-transparent rounded-full blur-3xl animate-float delay-1000"></div>

              <div className="relative grid md:grid-cols-2 gap-8 items-center">
                {/* Left side - Content */}
                <div className="space-y-6">
                  {/* Icon cluster */}
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl blur-xl opacity-60 group-hover:opacity-80 transition-opacity"></div>
                      <div className="relative bg-gradient-to-br from-emerald-400 to-teal-500 p-4 rounded-2xl shadow-lg">
                        <Beaker className="w-10 h-10 text-white" />
                      </div>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-2xl blur-xl opacity-60 group-hover:opacity-80 transition-opacity"></div>
                      <div className="relative bg-gradient-to-br from-teal-400 to-cyan-500 p-4 rounded-2xl shadow-lg">
                        <Activity className="w-10 h-10 text-white" />
                      </div>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl blur-xl opacity-60 group-hover:opacity-80 transition-opacity"></div>
                      <div className="relative bg-gradient-to-br from-cyan-400 to-blue-500 p-4 rounded-2xl shadow-lg">
                        <Cpu className="w-10 h-10 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Title & Badge */}
                  <div>
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="text-4xl md:text-5xl font-bold text-white drop-shadow-2xl">
                        BIOFUEL Digital Twin
                      </h3>
                      <span className="px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-bold rounded-full shadow-lg animate-pulse-soft">
                        FEATURED
                      </span>
                    </div>
                    <p className="text-xl md:text-2xl text-emerald-100 font-semibold drop-shadow-lg">
                      Hybrid Reactor Simulation & Implementation Platform
                    </p>
                  </div>

                  {/* Description */}
                  <p className="text-gray-100 text-lg leading-relaxed drop-shadow-md">
                    Advanced computational tools for simulating, optimizing, and implementing biofuel reactors.
                    Real-time digital twin technology for sustainable energy research and deployment.
                  </p>

                  {/* Feature badges */}
                  <div className="flex flex-wrap gap-3">
                    <div className="biofuel-badge">
                      <Droplets className="w-4 h-4" />
                      <span>Reactor Simulation</span>
                    </div>
                    <div className="biofuel-badge">
                      <Activity className="w-4 h-4" />
                      <span>Process Optimization</span>
                    </div>
                    <div className="biofuel-badge">
                      <Zap className="w-4 h-4" />
                      <span>Real-time Monitoring</span>
                    </div>
                    <div className="biofuel-badge">
                      <Cpu className="w-4 h-4" />
                      <span>Implementation Tools</span>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <button className="group/btn px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white rounded-xl font-bold text-lg shadow-2xl hover:shadow-emerald-500/60 transition-all duration-300 hover:scale-105 flex items-center space-x-2 border border-emerald-300/50">
                    <span>Explore Platform</span>
                    <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                </div>

                {/* Right side - Visual indicators */}
                <div className="relative h-80 md:h-96">
                  {/* Reactor visualization */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    {/* Central reactor core */}
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-full blur-3xl opacity-40 animate-pulse-slow"></div>
                      <div className="relative w-40 h-40 bg-gradient-to-br from-emerald-500/40 to-cyan-500/40 backdrop-blur-xl border-4 border-emerald-300/60 rounded-full flex items-center justify-center shadow-2xl">
                        <Beaker className="w-20 h-20 text-emerald-100" />
                      </div>
                    </div>

                    {/* Orbiting particles - representing data flow */}
                    <div className="absolute inset-0 animate-spin-slow">
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-4 h-4 bg-gradient-to-br from-emerald-300 to-emerald-400 rounded-full shadow-lg"></div>
                    </div>
                    <div className="absolute inset-0 animate-spin-slow delay-1000">
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-4 bg-gradient-to-br from-teal-300 to-teal-400 rounded-full shadow-lg"></div>
                    </div>
                    <div className="absolute inset-0 animate-spin-slow delay-500">
                      <div className="absolute top-1/2 right-0 -translate-y-1/2 w-4 h-4 bg-gradient-to-br from-cyan-300 to-cyan-400 rounded-full shadow-lg"></div>
                    </div>
                    <div className="absolute inset-0 animate-spin-slow delay-1500">
                      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-4 h-4 bg-gradient-to-br from-blue-300 to-blue-400 rounded-full shadow-lg"></div>
                    </div>

                    {/* Data streams */}
                    <div className="absolute top-1/4 right-1/4 w-32 h-32 border-2 border-emerald-300/30 rounded-full animate-ping-slow"></div>
                    <div className="absolute bottom-1/4 left-1/4 w-32 h-32 border-2 border-cyan-300/30 rounded-full animate-ping-slow delay-1000"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* AgriLED Digital Twin - Featured Card */}
          <div className="max-w-6xl mx-auto mt-16 mb-16 agriled-featured-card group">
            {/* Animated background glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-amber-400 via-orange-400 to-green-400 rounded-3xl blur-2xl opacity-20 group-hover:opacity-30 transition-opacity duration-500 animate-pulse-slow"></div>

            {/* Main card content */}
            <div className="relative bg-gradient-to-br from-amber-500/20 via-orange-500/20 to-green-500/20 backdrop-blur-xl border-2 border-amber-400/40 rounded-3xl p-8 md:p-12 shadow-2xl overflow-hidden">
              {/* Solar panel background image */}
              <div
                className="absolute inset-0 rounded-3xl opacity-10 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: 'url(/solar-farm-bg.png)' }}
              ></div>
              {/* Floating particles effect */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-amber-300/20 to-transparent rounded-full blur-3xl animate-float"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-green-300/20 to-transparent rounded-full blur-3xl animate-float delay-1000"></div>

              <div className="relative grid md:grid-cols-2 gap-8 items-center">
                {/* Left side - Content */}
                <div className="space-y-6">
                  {/* Icon cluster */}
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl blur-xl opacity-60 group-hover:opacity-80 transition-opacity"></div>
                      <div className="relative bg-gradient-to-br from-amber-400 to-orange-500 p-4 rounded-2xl shadow-lg">
                        <Zap className="w-10 h-10 text-white" />
                      </div>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-green-500 rounded-2xl blur-xl opacity-60 group-hover:opacity-80 transition-opacity"></div>
                      <div className="relative bg-gradient-to-br from-orange-400 to-green-500 p-4 rounded-2xl shadow-lg">
                        <Wheat className="w-10 h-10 text-white" />
                      </div>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl blur-xl opacity-60 group-hover:opacity-80 transition-opacity"></div>
                      <div className="relative bg-gradient-to-br from-green-400 to-emerald-500 p-4 rounded-2xl shadow-lg">
                        <Globe className="w-10 h-10 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Title & Badge */}
                  <div>
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="text-4xl md:text-5xl font-bold text-white drop-shadow-2xl">
                        AgriLED Digital Twin
                      </h3>
                      <span className="px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-bold rounded-full shadow-lg animate-pulse-soft">
                        FEATURED
                      </span>
                    </div>
                    <p className="text-xl md:text-2xl text-amber-100 font-semibold drop-shadow-lg">
                      Arctic Agrivoltaics + LED Horticulture Simulation Platform
                    </p>
                  </div>

                  {/* Description */}
                  <p className="text-gray-100 text-lg leading-relaxed drop-shadow-md">
                    3D digital twin demonstrating PV-agriculture synergy. Simulate Arctic solar radiation (69°N), crop photosynthesis, and multi-channel LED optimization in real-time. Prove that PV actively accelerates agriculture through microclimate modification and precision lighting.
                  </p>

                  {/* Feature badges */}
                  <div className="flex flex-wrap gap-3">
                    <div className="agriled-badge">
                      <Globe className="w-4 h-4" />
                      <span>3D Visualization</span>
                    </div>
                    <div className="agriled-badge">
                      <Zap className="w-4 h-4" />
                      <span>Arctic Solar Model</span>
                    </div>
                    <div className="agriled-badge">
                      <Wheat className="w-4 h-4" />
                      <span>Crop Photosynthesis</span>
                    </div>
                    <div className="agriled-badge">
                      <Lightbulb className="w-4 h-4" />
                      <span>LED Optimization</span>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <button className="group/btn px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white rounded-xl font-bold text-lg shadow-2xl hover:shadow-amber-500/60 transition-all duration-300 hover:scale-105 flex items-center space-x-2 border border-amber-300/50">
                    <span>Explore Platform</span>
                    <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                </div>

                {/* Right side - Visual indicators */}
                <div className="relative h-80 md:h-96">
                  {/* Solar + Agriculture visualization */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    {/* Central sun/solar core */}
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full blur-3xl opacity-40 animate-pulse-slow"></div>
                      <div className="relative w-40 h-40 bg-gradient-to-br from-amber-500/40 to-orange-500/40 backdrop-blur-xl border-4 border-amber-300/60 rounded-full flex items-center justify-center shadow-2xl">
                        <Zap className="w-20 h-20 text-amber-100" />
                      </div>
                    </div>

                    {/* Orbiting elements - representing PV panels, crops, LEDs */}
                    <div className="absolute inset-0 animate-spin-slow">
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-6 bg-gradient-to-br from-amber-400 to-orange-400 rounded-sm shadow-lg flex items-center justify-center">
                        <div className="w-4 h-4 bg-blue-900/60 border border-amber-200"></div>
                      </div>
                    </div>
                    <div className="absolute inset-0 animate-spin-slow delay-1000">
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-4 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full shadow-lg"></div>
                    </div>
                    <div className="absolute inset-0 animate-spin-slow delay-500">
                      <div className="absolute top-1/2 right-0 -translate-y-1/2 w-4 h-4 bg-gradient-to-br from-orange-300 to-orange-400 rounded-full shadow-lg"></div>
                    </div>
                    <div className="absolute inset-0 animate-spin-slow delay-1500">
                      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-4 h-4 bg-gradient-to-br from-yellow-300 to-yellow-400 rounded-full shadow-lg"></div>
                    </div>

                    {/* Energy/data streams */}
                    <div className="absolute top-1/4 right-1/4 w-32 h-32 border-2 border-amber-300/30 rounded-full animate-ping-slow"></div>
                    <div className="absolute bottom-1/4 left-1/4 w-32 h-32 border-2 border-green-300/30 rounded-full animate-ping-slow delay-1000"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ghatika Android App - Download Card */}
          <div className="max-w-4xl mx-auto mt-16 mb-16 ghatika-download-card group">
            {/* Animated background glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 rounded-3xl blur-2xl opacity-20 group-hover:opacity-30 transition-opacity duration-500 animate-pulse-slow"></div>

            {/* Main card content */}
            <div className="relative bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-xl border-2 border-indigo-400/40 rounded-3xl p-8 md:p-10 shadow-2xl overflow-hidden">
              {/* Clock background pattern */}
              <div className="absolute inset-0 rounded-3xl opacity-5 bg-cover bg-center bg-no-repeat"></div>

              {/* Floating particles effect */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-indigo-300/20 to-transparent rounded-full blur-3xl animate-float"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-pink-300/20 to-transparent rounded-full blur-3xl animate-float delay-1000"></div>

              <div className="relative flex flex-col md:flex-row gap-8 items-center">
                {/* Left side - Icon */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-3xl blur-2xl opacity-60 group-hover:opacity-80 transition-opacity"></div>
                    <div className="relative bg-gradient-to-br from-indigo-500 to-purple-600 p-8 rounded-3xl shadow-2xl">
                      <Clock className="w-20 h-20 text-white" />
                    </div>
                  </div>
                </div>

                {/* Right side - Content */}
                <div className="flex-1 space-y-4 text-center md:text-left">
                  {/* Title & Badge */}
                  <div>
                    <div className="flex flex-col md:flex-row items-center md:items-start space-y-2 md:space-y-0 md:space-x-3 mb-2">
                      <h3 className="text-3xl md:text-4xl font-bold text-white drop-shadow-2xl">
                        Ghatika (घटिका)
                      </h3>
                      <span className="px-3 py-1 bg-gradient-to-r from-green-400 to-emerald-400 text-white text-xs font-bold rounded-full shadow-lg animate-pulse-soft">
                        FREE ANDROID APP
                      </span>
                    </div>
                    <p className="text-lg md:text-xl text-indigo-100 font-semibold drop-shadow-lg">
                      Dual Timezone Clock + Smart Alarms
                    </p>
                  </div>

                  {/* Description */}
                  <p className="text-gray-100 leading-relaxed">
                    Track Kolkata and Glasgow time simultaneously. Set smart alarms with auto-dismiss, custom labels, and daily repeat. Your time companion with beautiful Arctic-themed interface.
                  </p>

                  {/* Feature badges */}
                  <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                    <div className="ghatika-badge">
                      <Clock className="w-4 h-4" />
                      <span>Dual Timezone</span>
                    </div>
                    <div className="ghatika-badge">
                      <Bell className="w-4 h-4" />
                      <span>Smart Alarms</span>
                    </div>
                    <div className="ghatika-badge">
                      <Smartphone className="w-4 h-4" />
                      <span>Android 7+</span>
                    </div>
                  </div>

                  {/* Download Button */}
                  <a
                    href="https://ahantravyuh.com/ghatika/apk/ghatika-latest.apk"
                    download
                    className="group/btn inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-400 hover:to-purple-400 text-white rounded-xl font-bold text-lg shadow-2xl hover:shadow-indigo-500/60 transition-all duration-300 hover:scale-105 border border-indigo-300/50"
                  >
                    <Download className="w-5 h-5" />
                    <span>Download APK (5.3 MB)</span>
                    <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                  </a>

                  <p className="text-xs text-indigo-200 drop-shadow-md">
                    Version 2.4.0 • Updated Feb 18, 2026
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Bar - Enhanced */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12">
            <div className="stat-card group">
              <div className="text-3xl font-bold text-yellow-300 mb-1 group-hover:scale-110 transition-transform">1000+</div>
              <div className="text-sm text-gray-200">Active Users</div>
            </div>
            <div className="stat-card group">
              <div className="text-3xl font-bold text-green-300 mb-1 group-hover:scale-110 transition-transform">46</div>
              <div className="text-sm text-gray-200">Apps</div>
            </div>
            <div className="stat-card group">
              <div className="text-3xl font-bold text-blue-300 mb-1 group-hover:scale-110 transition-transform">22</div>
              <div className="text-sm text-gray-200">Research Papers</div>
            </div>
            <div className="stat-card group">
              <div className="text-3xl font-bold text-purple-300 mb-1 group-hover:scale-110 transition-transform">300+</div>
              <div className="text-sm text-gray-200">Citations</div>
            </div>
          </div>
        </div>
      </div>

      {/* Apps Showcase */}
      <div className="relative py-20 px-4 z-10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-4 drop-shadow-2xl">
            Explore Our Ecosystem
          </h2>
          <p className="text-gray-100 text-center mb-12 text-lg drop-shadow-lg">
            Six interconnected applications, one unified experience
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Main App */}
            <div className="app-card from-yellow-500/20 to-orange-500/20 border-yellow-400/40 hover:border-yellow-300/70">
              <div className="bg-gradient-to-br from-yellow-400 to-orange-500 p-3 rounded-xl w-fit mb-4 shadow-lg">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-md">URJA Mining</h3>
              <p className="text-gray-100 text-sm leading-relaxed">
                Mine energy tokens, synthesize hydrogen, and power the ecosystem. Your time creates value.
              </p>
            </div>

            {/* ATAVI */}
            <div className="app-card from-green-500/20 to-emerald-500/20 border-green-400/40 hover:border-green-300/70">
              <div className="bg-gradient-to-br from-green-400 to-emerald-500 p-3 rounded-xl w-fit mb-4 shadow-lg">
                <TreePine className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-md">Atavi Forest</h3>
              <p className="text-gray-100 text-sm leading-relaxed">
                Manage your digital forest. Plant bamboo, teak, and sandalwood. Watch them grow over time.
              </p>
            </div>

            {/* Waahan */}
            <div className="app-card from-blue-500/20 to-cyan-500/20 border-blue-400/40 hover:border-blue-300/70">
              <div className="bg-gradient-to-br from-blue-400 to-cyan-500 p-3 rounded-xl w-fit mb-4 shadow-lg">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-md">Waahan Transit</h3>
              <p className="text-gray-100 text-sm leading-relaxed">
                Explore a 3D globe, manage vehicles, and complete e-cycle rides. Earn rice through sustainable transport.
              </p>
            </div>

            {/* Garud */}
            <div className="app-card from-red-500/20 to-orange-500/20 border-red-400/40 hover:border-red-300/70">
              <div className="bg-gradient-to-br from-red-400 to-orange-500 p-3 rounded-xl w-fit mb-4 shadow-lg">
                <Bot className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-md">Garud Arena</h3>
              <p className="text-gray-100 text-sm leading-relaxed">
                Build and battle robots. Strategic combat with offense, defense, and special abilities.
              </p>
            </div>

            {/* Vanijya */}
            <div className="app-card from-indigo-500/20 to-purple-500/20 border-indigo-400/40 hover:border-indigo-300/70">
              <div className="bg-gradient-to-br from-indigo-400 to-purple-500 p-3 rounded-xl w-fit mb-4 shadow-lg">
                <BarChart3 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-md">Vanijya Analytics</h3>
              <p className="text-gray-100 text-sm leading-relaxed">
                Track your economy, analyze trends, and manage your portfolio across all applications.
              </p>
            </div>

            {/* VIDYUT */}
            <div className="app-card from-cyan-500/20 to-blue-500/20 border-cyan-400/40 hover:border-cyan-300/70">
              <div className="bg-gradient-to-br from-cyan-400 to-blue-500 p-3 rounded-xl w-fit mb-4 shadow-lg">
                <Gamepad2 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-md">Vidyut Energy</h3>
              <p className="text-gray-100 text-sm leading-relaxed">
                Blockchain-based energy game. Scavenge materials and learn about renewable energy systems.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Complete App Catalog - App Store */}
      <div className="relative py-20 px-4 z-10 bg-gradient-to-b from-transparent via-black/20 to-transparent">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-4 drop-shadow-2xl">
            Complete App Catalog
          </h2>
          <p className="text-gray-100 text-center mb-12 text-lg drop-shadow-lg">
            46 applications across 11 categories — all accessible with one account
          </p>

          {/* Games & Entertainment */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center drop-shadow-lg">
              <Gamepad2 className="w-6 h-6 mr-3 text-purple-400" />
              Games & Entertainment
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="app-store-card">
                <Bot className="w-10 h-10 text-red-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Garud</h4>
              </div>
              <div className="app-store-card">
                <Globe className="w-10 h-10 text-blue-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Waahan</h4>
              </div>
              <div className="app-store-card">
                <TreePine className="w-10 h-10 text-green-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Atavi</h4>
              </div>
              <div className="app-store-card">
                <Gamepad2 className="w-10 h-10 text-indigo-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Chaturang</h4>
              </div>
              <div className="app-store-card">
                <Trophy className="w-10 h-10 text-yellow-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Krida</h4>
              </div>
            </div>
          </div>

          {/* Vedic Learning */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center drop-shadow-lg">
              <BookOpen className="w-6 h-6 mr-3 text-orange-400" />
              Vedic Learning
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="app-store-card">
                <BookOpen className="w-10 h-10 text-orange-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Rigveda</h4>
              </div>
              <div className="app-store-card">
                <Music className="w-10 h-10 text-pink-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Samveda</h4>
              </div>
              <div className="app-store-card">
                <Scroll className="w-10 h-10 text-amber-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Yajurveda</h4>
              </div>
              <div className="app-store-card">
                <Sparkles className="w-10 h-10 text-violet-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Atharvaveda</h4>
              </div>
              <div className="app-store-card">
                <Book className="w-10 h-10 text-blue-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Ved</h4>
              </div>
            </div>
          </div>

          {/* Business & Economy */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center drop-shadow-lg">
              <BarChart3 className="w-6 h-6 mr-3 text-emerald-400" />
              Business & Economy
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="app-store-card">
                <BarChart3 className="w-10 h-10 text-indigo-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Vanijya</h4>
              </div>
              <div className="app-store-card">
                <Warehouse className="w-10 h-10 text-slate-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Bhandar</h4>
              </div>
              <div className="app-store-card">
                <ShoppingCart className="w-10 h-10 text-teal-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Aapanam</h4>
              </div>
              <div className="app-store-card">
                <Coins className="w-10 h-10 text-yellow-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Selvam</h4>
              </div>
              <div className="app-store-card">
                <TrendingUp className="w-10 h-10 text-green-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Wari</h4>
              </div>
              <div className="app-store-card">
                <Wheat className="w-10 h-10 text-amber-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Vrihi</h4>
              </div>
            </div>
          </div>

          {/* Transportation */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center drop-shadow-lg">
              <Plane className="w-6 h-6 mr-3 text-sky-400" />
              Transportation
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="app-store-card">
                <Plane className="w-10 h-10 text-blue-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Helicopter</h4>
              </div>
              <div className="app-store-card">
                <Train className="w-10 h-10 text-red-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Train</h4>
              </div>
              <div className="app-store-card">
                <Anchor className="w-10 h-10 text-cyan-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Ship</h4>
              </div>
              <div className="app-store-card">
                <Plane className="w-10 h-10 text-indigo-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Plane</h4>
              </div>
              <div className="app-store-card">
                <Rocket className="w-10 h-10 text-purple-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Jet</h4>
              </div>
            </div>
          </div>

          {/* Science & Research */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center drop-shadow-lg">
              <Atom className="w-6 h-6 mr-3 text-cyan-400" />
              Science & Research
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="app-store-card">
                <Globe className="w-10 h-10 text-blue-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Medhini</h4>
              </div>
              <div className="app-store-card">
                <Atom className="w-10 h-10 text-cyan-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Dravya</h4>
              </div>
              <div className="app-store-card">
                <Gem className="w-10 h-10 text-purple-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Khanij</h4>
              </div>
              <div className="app-store-card">
                <Flame className="w-10 h-10 text-orange-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Ushna</h4>
              </div>
              <div className="app-store-card">
                <Star className="w-10 h-10 text-yellow-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Nakshatra</h4>
              </div>
              <div className="app-store-card">
                <Wheat className="w-10 h-10 text-amber-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">AgriLED</h4>
              </div>
              <div className="app-store-card">
                <Activity className="w-10 h-10 text-green-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Uyir</h4>
              </div>
            </div>
          </div>

          {/* Knowledge & Learning */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center drop-shadow-lg">
              <GraduationCap className="w-6 h-6 mr-3 text-green-400" />
              Knowledge & Learning
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="app-store-card">
                <GraduationCap className="w-10 h-10 text-green-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Gyanodbhaw</h4>
              </div>
              <div className="app-store-card">
                <Languages className="w-10 h-10 text-blue-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Padavali</h4>
              </div>
              <div className="app-store-card">
                <Book className="w-10 h-10 text-orange-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Kural</h4>
              </div>
              <div className="app-store-card">
                <Heart className="w-10 h-10 text-pink-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Ramakrishna</h4>
              </div>
              <div className="app-store-card">
                <Languages className="w-10 h-10 text-emerald-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Bangla</h4>
              </div>
            </div>
          </div>

          {/* Tools & Utilities */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center drop-shadow-lg">
              <Settings className="w-6 h-6 mr-3 text-slate-400" />
              Tools & Utilities
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="app-store-card">
                <Map className="w-10 h-10 text-red-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Maanchitra</h4>
              </div>
              <div className="app-store-card">
                <FileText className="w-10 h-10 text-blue-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">CV</h4>
              </div>
              <div className="app-store-card">
                <Eye className="w-10 h-10 text-purple-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Vizhi</h4>
              </div>
              <div className="app-store-card">
                <Scan className="w-10 h-10 text-teal-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Drishman</h4>
              </div>
              <div className="app-store-card">
                <DoorOpen className="w-10 h-10 text-indigo-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Gate</h4>
              </div>
              <div className="app-store-card">
                <Clock className="w-10 h-10 text-violet-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Ghatika</h4>
              </div>
              <div className="app-store-card">
                <FileText className="w-10 h-10 text-amber-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Chitragupt</h4>
              </div>
            </div>
          </div>

          {/* Communication */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center drop-shadow-lg">
              <MessageSquare className="w-6 h-6 mr-3 text-blue-400" />
              Communication
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="app-store-card">
                <MessageSquare className="w-10 h-10 text-blue-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Forum</h4>
              </div>
              <div className="app-store-card">
                <Mail className="w-10 h-10 text-cyan-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Doota</h4>
              </div>
              <div className="app-store-card">
                <Mail className="w-10 h-10 text-purple-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Doota Creator</h4>
              </div>
            </div>
          </div>

          {/* Infrastructure & Services */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center drop-shadow-lg">
              <Shield className="w-6 h-6 mr-3 text-red-400" />
              Infrastructure & Services
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="app-store-card">
                <Shield className="w-10 h-10 text-orange-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Hanuman</h4>
              </div>
              <div className="app-store-card">
                <Users className="w-10 h-10 text-green-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Sashrayi</h4>
              </div>
              <div className="app-store-card">
                <Lightbulb className="w-10 h-10 text-yellow-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Janayitri</h4>
              </div>
              <div className="app-store-card">
                <Settings className="w-10 h-10 text-slate-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Ayvakam</h4>
              </div>
              <div className="app-store-card">
                <Cloud className="w-10 h-10 text-blue-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Rtudhwaj</h4>
              </div>
              <div className="app-store-card">
                <Cpu className="w-10 h-10 text-indigo-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">ScotIoT</h4>
              </div>
            </div>
          </div>

          {/* News & Media */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center drop-shadow-lg">
              <Newspaper className="w-6 h-6 mr-3 text-gray-400" />
              News & Media
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="app-store-card">
                <Newspaper className="w-10 h-10 text-gray-300 mb-2" />
                <h4 className="text-white font-semibold text-sm">Shankhyarava</h4>
              </div>
              <div className="app-store-card">
                <Utensils className="w-10 h-10 text-orange-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Annapoorna</h4>
              </div>
            </div>
          </div>

          {/* Core Platform */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center drop-shadow-lg">
              <Zap className="w-6 h-6 mr-3 text-yellow-400" />
              Core Platform
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="app-store-card">
                <Zap className="w-10 h-10 text-yellow-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">AhantraVyuh</h4>
              </div>
              <div className="app-store-card">
                <ArrowRightLeft className="w-10 h-10 text-purple-400 mb-2" />
                <h4 className="text-white font-semibold text-sm">Ahant</h4>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-16">
            <div className="inline-block bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-sm border border-purple-400/30 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-3 drop-shadow-lg">
                One Account, 46 Applications
              </h3>
              <p className="text-gray-100 mb-6 max-w-2xl">
                Access the entire ecosystem with a single login. Start exploring today!
              </p>
              <button
                onClick={onRegister}
                className="px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold text-lg shadow-2xl hover:shadow-purple-500/60 transition-all duration-300 hover:scale-105 border border-purple-400/30 hover:border-purple-400/60"
              >
                Get Started Free
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Why AhantraVyuh Section */}
      <div className="relative py-20 px-4 bg-black/30 backdrop-blur-sm z-10 border-y border-white/10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-12 drop-shadow-2xl">
            Why AhantraVyuh?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="text-center feature-card">
              <div className="bg-gradient-to-br from-purple-400 to-indigo-500 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center shadow-xl">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 drop-shadow-md">Swarm Intelligence</h3>
              <p className="text-gray-100 leading-relaxed">
                46 applications working together as a unified intelligence ecosystem. Collective problem-solving at scale.
              </p>
            </div>

            <div className="text-center feature-card">
              <div className="bg-gradient-to-br from-cyan-400 to-blue-500 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center shadow-xl">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 drop-shadow-md">Digital Twin Platform</h3>
              <p className="text-gray-100 leading-relaxed">
                Real-time digital replicas of systems and processes. Experience the future of interconnected applications.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer / About Section */}
      <footer className="relative py-12 px-4 bg-black/60 backdrop-blur-md border-t border-white/20 z-10">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            {/* Company Info */}
            <div className="max-w-2xl mx-auto text-center">
              <div className="flex items-center justify-center mb-4">
                <img src="/ahantra.png" alt="AhantraVyuh" className="w-8 h-8 mr-2" />
                <h3 className="text-xl font-bold text-white drop-shadow-md">About Us</h3>
              </div>
              <p className="text-gray-100 mb-4 leading-relaxed">
                <strong className="text-white">AHANTRAVYUH LTD</strong> is a Scottish registered company pioneering an integrated Swarm Intelligence powered Digital Twin platform.
              </p>
              <div className="space-y-2 text-sm text-gray-300">
                <p><strong className="text-gray-100">Company Number:</strong> SC866275</p>
                <p><strong className="text-gray-100">Registration Date:</strong> October 14, 2025</p>
                <p><strong className="text-gray-100">Type:</strong> Private Limited by Shares</p>
                <p><strong className="text-gray-100">Registered in:</strong> Scotland, United Kingdom</p>
              </div>
            </div>
          </div>

          {/* Social Media Links */}
          <div className="border-t border-white/20 pt-8 pb-8 text-center">
            <p className="text-gray-100 mb-4 font-semibold">Find us on</p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="https://x.com/ahantravyuh"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 hover:from-blue-500/30 hover:to-cyan-500/30 backdrop-blur-sm border border-blue-400/30 hover:border-blue-400/60 rounded-xl text-white font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-blue-500/40"
              >
                <Twitter className="w-5 h-5" />
                <span>@ahantravyuh</span>
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61583048518823"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600/20 to-indigo-600/20 hover:from-blue-600/30 hover:to-indigo-600/30 backdrop-blur-sm border border-blue-500/30 hover:border-blue-500/60 rounded-xl text-white font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-blue-600/40"
              >
                <Facebook className="w-5 h-5" />
                <span>AhantraVyuh</span>
              </a>
            </div>
          </div>

          {/* Mission Statement */}
          <div className="border-t border-white/20 pt-8 text-center">
            <p className="text-gray-300 italic mb-4 drop-shadow-md">
              "Pioneering the convergence of Swarm Intelligence and Digital Twin technology to create a unified platform of collaborative applications"
            </p>
            <p className="text-gray-400 text-sm">
              © 2025 AHANTRAVYUH LTD. All rights reserved. | Empowering the future through intelligent, interconnected systems.
            </p>
            <p className="text-gray-500 text-xs mt-3">
              <a href="https://gyn.ahantravyuh.com/compliance.html" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 underline underline-offset-2 transition-colors duration-200">Regulatory Compliance</a>
              &nbsp;·&nbsp;
              <a href="https://gyn.ahantravyuh.com/terms.html" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 underline underline-offset-2 transition-colors duration-200">Terms of Service</a>
              &nbsp;·&nbsp;
              <a href="https://gyn.ahantravyuh.com/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 underline underline-offset-2 transition-colors duration-200">Privacy Policy</a>
              &nbsp;·&nbsp; SC866275 &nbsp;·&nbsp; ICO C1925848
            </p>
            <p className="text-gray-600 text-xs mt-2">
              Platform operated under UK law (Scotland). Users outside the UK should note that regulatory protections may differ in their jurisdiction.
            </p>
          </div>
        </div>
      </footer>

      {/* Enhanced Custom Animations & Styles - Vibrant Gradient from Core Homepage */}
      <style>{`
        /* Vibrant Animated Gradient Background */
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          25% { background-position: 100% 50%; }
          50% { background-position: 50% 100%; }
          75% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        @keyframes colorSplash {
          0%, 100% { opacity: 0.4; transform: scale(1) rotate(0deg); }
          50% { opacity: 0.7; transform: scale(1.2) rotate(180deg); }
        }

        .animated-gradient-bg {
          background: linear-gradient(-45deg,
            #ff006e, /* Hot Pink */
            #fb5607, /* Orange */
            #ffbe0b, /* Yellow */
            #8338ec, /* Purple */
            #3a86ff, /* Blue */
            #06ffa5, /* Mint Green */
            #ff006e, /* Hot Pink */
            #fb5607  /* Orange */
          );
          background-size: 400% 400%;
          animation: gradientShift 20s ease infinite;
          position: relative;
        }

        .color-splash-layer-1 {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-image:
            radial-gradient(circle at 10% 20%, rgba(255, 0, 110, 0.3) 0%, transparent 25%),
            radial-gradient(circle at 90% 80%, rgba(251, 86, 7, 0.3) 0%, transparent 25%),
            radial-gradient(circle at 50% 50%, rgba(255, 190, 11, 0.25) 0%, transparent 30%),
            radial-gradient(circle at 80% 20%, rgba(131, 56, 236, 0.3) 0%, transparent 25%),
            radial-gradient(circle at 20% 80%, rgba(58, 134, 255, 0.3) 0%, transparent 25%),
            radial-gradient(circle at 70% 60%, rgba(6, 255, 165, 0.25) 0%, transparent 30%);
          animation: colorSplash 8s ease-in-out infinite;
          pointer-events: none;
          z-index: 0;
        }

        .color-splash-layer-2 {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-image:
            radial-gradient(circle at 40% 40%, rgba(255, 0, 110, 0.25) 0%, transparent 20%),
            radial-gradient(circle at 60% 70%, rgba(251, 86, 7, 0.25) 0%, transparent 20%),
            radial-gradient(circle at 30% 60%, rgba(131, 56, 236, 0.25) 0%, transparent 20%),
            radial-gradient(circle at 75% 35%, rgba(58, 134, 255, 0.25) 0%, transparent 20%),
            radial-gradient(circle at 85% 85%, rgba(6, 255, 165, 0.25) 0%, transparent 20%),
            radial-gradient(circle at 15% 15%, rgba(255, 190, 11, 0.25) 0%, transparent 20%);
          animation: colorSplash 10s ease-in-out infinite reverse;
          pointer-events: none;
          z-index: 0;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse-slow {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.05); }
        }

        @keyframes pulse-slower {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.05); }
        }

        @keyframes pulse-soft {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 1; }
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out;
        }

        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }

        .animate-pulse-slower {
          animation: pulse-slower 6s ease-in-out infinite;
        }

        .animate-pulse-soft {
          animation: pulse-soft 2s ease-in-out infinite;
        }

        .delay-500 {
          animation-delay: 0.5s;
        }

        .delay-1000 {
          animation-delay: 1s;
        }

        .stat-card {
          background: rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(20px);
          border-radius: 1rem;
          padding: 1rem;
          border: 1px solid rgba(255, 255, 255, 0.15);
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          background: rgba(255, 255, 255, 0.12);
          border-color: rgba(255, 255, 255, 0.3);
          transform: translateY(-2px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        }

        .app-card {
          background: linear-gradient(135deg, var(--tw-gradient-stops));
          backdrop-filter: blur(20px);
          border-radius: 1.5rem;
          padding: 1.5rem;
          border: 1px solid;
          transition: all 0.3s ease;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }

        .app-card:hover {
          transform: translateY(-5px) scale(1.02);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);
        }

        .feature-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(15px);
          border-radius: 1rem;
          padding: 1.5rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
        }

        .feature-card:hover {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 255, 255, 0.2);
          transform: translateY(-3px);
        }

        .app-store-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(15px);
          border-radius: 1rem;
          padding: 1.25rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
          text-align: center;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .app-store-card:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.25);
          transform: translateY(-4px) scale(1.03);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
        }

        /* BIOFUEL Digital Twin Card Styles */
        .biofuel-featured-card {
          position: relative;
          transition: all 0.5s ease;
        }

        .biofuel-featured-card:hover {
          transform: translateY(-5px);
        }

        .biofuel-badge {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: rgba(16, 185, 129, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(16, 185, 129, 0.4);
          border-radius: 9999px;
          color: rgb(209, 250, 229);
          font-size: 0.875rem;
          font-weight: 600;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
        }

        .biofuel-badge:hover {
          background: rgba(16, 185, 129, 0.3);
          border-color: rgba(16, 185, 129, 0.6);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(16, 185, 129, 0.3);
        }

        /* AgriLED Digital Twin Card Styles */
        .agriled-featured-card {
          position: relative;
          transition: all 0.5s ease;
        }

        .agriled-featured-card:hover {
          transform: translateY(-5px);
        }

        .agriled-badge {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: rgba(245, 158, 11, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(245, 158, 11, 0.4);
          border-radius: 9999px;
          color: rgb(254, 243, 199);
          font-size: 0.875rem;
          font-weight: 600;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
        }

        .agriled-badge:hover {
          background: rgba(245, 158, 11, 0.3);
          border-color: rgba(245, 158, 11, 0.6);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(245, 158, 11, 0.3);
        }

        /* Slow spinning animation for orbiting particles */
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }

        /* Slow ping animation for data streams */
        @keyframes ping-slow {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          75%, 100% {
            transform: scale(2);
            opacity: 0;
          }
        }

        .animate-ping-slow {
          animation: ping-slow 3s cubic-bezier(0, 0, 0.2, 1) infinite;
        }

        /* Ghatika App Download Card Styles */
        .ghatika-download-card {
          position: relative;
          transition: all 0.5s ease;
        }

        .ghatika-download-card:hover {
          transform: translateY(-5px);
        }

        .ghatika-badge {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: rgba(99, 102, 241, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(99, 102, 241, 0.4);
          border-radius: 9999px;
          color: rgb(224, 231, 255);
          font-size: 0.875rem;
          font-weight: 600;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
        }

        .ghatika-badge:hover {
          background: rgba(99, 102, 241, 0.3);
          border-color: rgba(99, 102, 241, 0.6);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(99, 102, 241, 0.3);
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
